import type { Employment, MonthYear } from "@components/quick_apply/employments";
import { Education } from "@components/quick_apply/education_section.tsx";
import { FileState } from "@/queries/use_profile.ts";

export interface EmploymentError {
  company?: string;
  title?: string;
  startDateMonth?: string;
  startDateYear?: string;
  endDateMonth?: string;
  endDateYear?: string;
  endDateYearInvalid?: string;
}

export interface EmploymentErrors {
  [index: number]: EmploymentError;
}

export interface EducationError {
  startDateYear?: string;
  endDateYear?: string;
}

export interface EducationErrors {
  [index: number]: EducationError;
}

// Must match DateRangeValidatable::YEAR_RANGE on the backend
export const MIN_YEAR = 1900;
export const MAX_YEAR = 2100;

function isYearOutOfRange(year?: number | null): boolean {
  return year != null && !Number.isNaN(year) && (year < MIN_YEAR || year > MAX_YEAR);
}

function isStartDateInFuture(month?: number | null, year?: number | null): boolean {
  if (year == null) {
    return false;
  }

  const now = new Date();
  const currentYear = now.getFullYear();

  if (year > currentYear) {
    return true;
  }

  return year === currentYear && month != null && month > now.getMonth() + 1;
}

function isEmptyMonthYear(monthYear?: MonthYear | null): boolean {
  if (!monthYear) {
    return true;
  }

  return !monthYear.month && !monthYear.year;
}

function isEmptyEmployment(employment: Partial<Employment>): boolean {
  return (
    !employment.company &&
    !employment.title &&
    isEmptyMonthYear(employment.startDate) &&
    isEmptyMonthYear(employment.endDate)
  );
}

export function endDateAfterStartDate(
  startMonth?: number | null,
  startYear?: number | null,
  endMonth?: number | null,
  endYear?: number | null,
): boolean {
  if (startYear != null && endYear != null) {
    if (startYear > endYear) {
      return false;
    } else if (startYear === endYear) {
      if (startMonth == null || endMonth == null) {
        return true;
      } else if (startMonth >= endMonth) {
        return false;
      }
    }
  }

  return true;
}

export function validateEmployments(employments: Partial<Employment>[]): EmploymentErrors {
  const errors: EmploymentErrors = {};

  employments.forEach((employment, index) => {
    const error = {} as EmploymentError;

    if (isEmptyEmployment(employment)) {
      return;
    }

    if (!employment.company?.trim()) {
      error.company = "employment.companyRequired";
    }

    if (!employment.title?.trim()) {
      error.title = "employment.titleRequired";
    }

    if (!employment.startDate?.month) {
      error.startDateMonth = "dates.startDateMonthRequired";
    }

    if (!employment.startDate?.year) {
      error.startDateYear = "dates.startDateYearRequired";
    } else if (isYearOutOfRange(employment.startDate.year)) {
      error.startDateYear = "dates.yearRangeInvalid";
    } else if (isStartDateInFuture(employment.startDate.month, employment.startDate.year)) {
      error.startDateYear = "dates.startDateError";
    }

    // We only need to validate end date if either field is provided.
    // Otherwise we can consider this a current employment.
    if (!isEmptyMonthYear(employment.endDate)) {
      if (!employment.endDate?.month) {
        error.endDateMonth = "dates.endDateMonthRequired";
      } else if (!employment.endDate?.year) {
        error.endDateYear = "dates.endDateYearRequired";
      } else if (isYearOutOfRange(employment.endDate.year)) {
        error.endDateYear = "dates.yearRangeInvalid";
      }
    }

    if (
      !endDateAfterStartDate(
        employment.startDate?.month,
        employment.startDate?.year,
        employment.endDate?.month,
        employment.endDate?.year,
      )
    ) {
      error.endDateYearInvalid = "dates.dateRangeInvalid";
    }

    if (Object.keys(error).length > 0) {
      errors[index] = error;
    }
  });

  return errors;
}

export const ALLOWED_FILETYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/rtf",
  "application/rtf",
];

export const MAX_FILE_SIZE_MB = 100;

const megabytesToBytes = (mb: number) => mb * 1024 * 1024;

export const validateResume = (resume: FileState | null): undefined | string => {
  if (!resume?.file) {
    return;
  }

  if (!ALLOWED_FILETYPES.includes(resume.file.type)) {
    return "resume.invalidFileType";
  }

  if (resume.file.size > megabytesToBytes(MAX_FILE_SIZE_MB)) {
    return "resume.maxFileSize";
  }
};

export const validateEducations = (educations: Array<Education>): EducationErrors => {
  const errors: EducationErrors = {};

  educations.forEach((education: Education, index: number) => {
    const error = {} as EducationError;

    if (isYearOutOfRange(education.startYear)) {
      error.startDateYear = "dates.yearRangeInvalid";
    } else if (isStartDateInFuture(education.startMonth, education.startYear)) {
      error.startDateYear = "dates.startDateError";
    }

    if (isYearOutOfRange(education.endYear)) {
      error.endDateYear = "dates.yearRangeInvalid";
    } else if (
      !endDateAfterStartDate(
        education.startMonth,
        education.startYear,
        education.endMonth,
        education.endYear,
      )
    ) {
      error.endDateYear = "dates.dateRangeInvalid";
    }

    if (Object.keys(error).length > 0) {
      errors[index] = error;
    }
  });

  return errors;
};
